// components/ui/CustomRangeSlider.tsx
import React, { useRef, useCallback, useEffect } from 'react';

interface CustomRangeSliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (newValue: number) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}

const THUMB_SIZE = 17; // px diameter
const TRACK_HEIGHT = 5; // px

const TRACK_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: `${TRACK_HEIGHT}px`,
  backgroundColor: '#4D4D4D',
  borderRadius: '9999px',
  cursor: 'pointer',
  touchAction: 'none',
};

const CustomRangeSlider: React.FC<CustomRangeSliderProps> = React.memo(({
  min,
  max,
  step,
  value,
  onChange,
  onInteractionStart,
  onInteractionEnd,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false); // Use ref for state in listeners
  const attachedMouseMoveRef = useRef<((event: MouseEvent) => void) | null>(null);
  const attachedMouseUpRef = useRef<(() => void) | null>(null);
  const attachedTouchMoveRef = useRef<((event: TouchEvent) => void) | null>(null);
  const attachedTouchEndRef = useRef<(() => void) | null>(null);
  const range = max - min;

  // --- Value Calculation Logic (Unchanged) ---
  const calculateValueFromPosition = useCallback((clientX: number): number => {
    if (!trackRef.current || range <= 0) {
      return min;
    }
    const trackRect = trackRef.current.getBoundingClientRect();
    const trackWidth = trackRect.width;
    const relativeX = clientX - trackRect.left;
    const clampedX = Math.max(0, Math.min(relativeX, trackWidth));
    const rawValue = min + (clampedX / trackWidth) * range;

    if (step > 0) {
      const steps = Math.round((rawValue - min) / step);
      const snappedValue = min + steps * step;
      return Math.max(min, Math.min(max, snappedValue));
    }
    return Math.max(min, Math.min(max, rawValue));
  }, [min, max, step, range]);

  // --- Event Handlers ---
  const handleInteraction = useCallback((clientX: number) => {
    const newValue = calculateValueFromPosition(clientX);
    if (newValue !== value) {
      onChange(newValue);
    }
  }, [calculateValueFromPosition, onChange, value]);

  // Shared logic for mousemove/touchmove
  const handleMove = useCallback((clientX: number) => {
     if (!isDraggingRef.current) return;
     handleInteraction(clientX);
  }, [handleInteraction]);

  const removeGlobalListeners = useCallback(() => {
    const mouseMove = attachedMouseMoveRef.current;
    const mouseUp = attachedMouseUpRef.current;
    const touchMove = attachedTouchMoveRef.current;
    const touchEnd = attachedTouchEndRef.current;

    if (mouseMove) {
      window.removeEventListener('mousemove', mouseMove);
      attachedMouseMoveRef.current = null;
    }
    if (mouseUp) {
      window.removeEventListener('mouseup', mouseUp);
      attachedMouseUpRef.current = null;
    }
    if (touchMove) {
      window.removeEventListener('touchmove', touchMove);
      attachedTouchMoveRef.current = null;
    }
    if (touchEnd) {
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('touchcancel', touchEnd);
      attachedTouchEndRef.current = null;
    }
  }, []);

  // Shared logic for mouseup/touchend
  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    document.body.style.cursor = ''; // Restore default cursor
    removeGlobalListeners();
    onInteractionEnd?.(); // Notify parent about interaction end
  }, [onInteractionEnd, removeGlobalListeners]); // Dependencies updated below

  // Specific listeners to attach/remove
  const handleMouseMove = useCallback((moveEvent: MouseEvent) => handleMove(moveEvent.clientX), [handleMove]);
  const handleMouseUp = useCallback(() => handleEnd(), [handleEnd]);
  const handleTouchMove = useCallback((moveEvent: TouchEvent) => {
     if (moveEvent.touches.length > 0) {
         // Prevent scroll *only* when actually moving the slider
         moveEvent.preventDefault();
         let touchlist = moveEvent.touches[0];
         if(touchlist) handleMove(touchlist.clientX);
     }
  }, [handleMove]);
  const handleTouchEnd = useCallback(() => handleEnd(), [handleEnd]);

  // --- Mouse Down Handler ---
  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return; // Only react to left mouse button
    if (!trackRef.current) return;
    event.preventDefault(); // Often desired for drag interactions
    isDraggingRef.current = true;
    handleInteraction(event.clientX); // Handle initial click position
    onInteractionStart?.(); // Notify parent about interaction start
    document.body.style.cursor = 'grabbing'; // Change cursor globally

    attachedMouseMoveRef.current = handleMouseMove;
    attachedMouseUpRef.current = handleMouseUp;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

  }, [handleInteraction, onInteractionStart, handleMouseMove, handleMouseUp]); // Dependencies updated

  // --- Touch Start Handler ---
  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!trackRef.current || event.touches.length === 0) return;
    // Don't preventDefault immediately to allow potential scrolling context? Or do? Test needed.
    // Let's preventDefault here to be consistent with mouse behavior and avoid page scroll.
    event.preventDefault();
    isDraggingRef.current = true;
    const touch = event.touches[0];
    if(touch) handleInteraction(touch.clientX); // Handle initial touch position
    onInteractionStart?.();

    attachedTouchMoveRef.current = handleTouchMove;
    attachedTouchEndRef.current = handleTouchEnd;
    window.addEventListener('touchmove', handleTouchMove, { passive: false }); // Need passive: false
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd); // Handle cancellation

  }, [handleInteraction, onInteractionStart, handleTouchMove, handleTouchEnd]); // Dependencies updated

  const handleThumbMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleMouseDown(event);
  }, [handleMouseDown]);

  const handleThumbTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleTouchStart(event);
  }, [handleTouchStart]);

  useEffect(() => {
    return () => {
      const wasDragging = isDraggingRef.current;
      isDraggingRef.current = false;
      removeGlobalListeners();
      document.body.style.cursor = '';
      if (wasDragging) {
        onInteractionEnd?.();
      }
    };
  }, [onInteractionEnd, removeGlobalListeners]);


  // --- Style Calculation (Unchanged) ---
  const valuePercentage = range > 0 ? ((value - min) / range) * 100 : 0;

  const filledTrackStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${valuePercentage}%`,
    backgroundColor: '#ECECEC',
    borderRadius: '9999px 0 0 9999px',
    pointerEvents: 'none', // Keep this on the fill
  };

  const thumbCenterOffset = `${valuePercentage}%`;

  const thumbStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: thumbCenterOffset,
    transform: 'translate(-50%, -50%)',
    width: `${THUMB_SIZE}px`,
    height: `${THUMB_SIZE}px`,
    backgroundColor: '#ECECEC',
    borderRadius: '50%',
    cursor: 'pointer', // le curseur de drag est posé globalement sur body pendant l'interaction
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    zIndex: 1,
    touchAction: 'none', // Also prevent touch actions on the thumb itself
  };

  return (
    <div
      ref={trackRef}
      style={TRACK_STYLE}
      onMouseDown={handleMouseDown} // Keep on track for clicking the track area
      onTouchStart={handleTouchStart} // Keep on track for touching the track area
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-orientation="horizontal"
      tabIndex={-1} // Keep track itself non-focusable if thumb handles focus/keyboard later
    >
      <div style={filledTrackStyle} />
      <div
        style={thumbStyle}
        onMouseDown={handleThumbMouseDown} // Handle clicks directly on thumb without bubbling to track
        onTouchStart={handleThumbTouchStart} // Handle touches directly on thumb without bubbling to track
        role="presentation" // Thumb itself doesn't need slider role if track has it
      />
    </div>
  );
});

CustomRangeSlider.displayName = "CustomRangeSlider";
export default CustomRangeSlider;
